import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyIcon } from './icons';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { StyledFlex } from 'src/components/StyledFlex';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { useProductActions } from './hooks';
import CopyProductModal from './modals';
import { HiddenIconButton } from 'src/views/Pages/ESG/ReportsIndex/index.styles';
import { toast } from 'react-toastify';
import { LcaProductMaterial } from 'src/Lca/types';
import TextWithTooltip from 'src/components/TextWithTooltip';
import {
  DownloadOutlined,
  FolderOutlined,
  ShareOutlined,
} from '@mui/icons-material';
import {
  ProductImpactOutSchema,
  LcaDiagramColumns,
} from 'src/Lca/LCADiagram/Diagram/index.types';
import EcoinventEmission from 'src/Lca/components/EcoinventEmission';
import { getLcaApiErrorMessage } from 'src/Lca/apiErrors';

const Box = css`
  border-radius: 16px;
  border: 3px solid var(--Stroke, #dadada);
  background: var(--White, #fff);
  text-decoration: none;
  padding: 34px 30px 16px;
  display: flex;
  flex-direction: column;
  width: 456px;
  min-height: 400px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);

  &:hover {
    border: 3px solid var(--brand-color-hover, #8f5409);
    background: var(--brand-color-light, #fff7e8);
    //  TODO: Ten dziwny cień na dole
  }

  &:active {
    box-shadow: none;
    border: 3px solid var(--brand-color-hover, #8f5409);
    background: var(--brand-color-light, #fff7e8);
  }
`;

interface BoxItemInnerProps extends React.HTMLProps<HTMLDivElement> {
  $buttonVisible: boolean;
  $selected?: boolean;
}

const BoxLink = styled(Link)`
  ${Box}
`;

const BoxDiv = styled.div<BoxItemInnerProps>`
  ${Box}
  position: relative;
  ${(props) =>
    props.$selected &&
    css`
      border: 3px solid var(--brand-color-main, #7e4308);
      background: var(--brand-color-light, #fff7e8);
    `}

  a {
    text-decoration: none;
  }
    .MuiIconButton-root {
    visibility: ${(props) =>
      props.$buttonVisible ? 'visible !important' : 'hidden'};
  }
      &:hover {
      & .MuiIconButton-root {
    visibility: visible;
  }
`;

const SelectCheckbox = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;

  .MuiCheckbox-root {
    visibility: visible !important;
  }
`;

const Text = styled.p`
  color: var(--Dark, #281302);
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: 0.48px;
  margin-bottom: 0;
  margin-right: 80px; /* Leave space for thumbnail */
`;

const FolderLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 80px 0 0; /* Leave space for thumbnail */
  font-size: 13px;
  color: #6e6e6e;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: none;
  }
`;

type ProductsItemsEmptyProps = {
  canCreate: boolean;
};

export function ProductsItemsEmpty(props: ProductsItemsEmptyProps) {
  const { t } = useTranslation('lca');
  const link = '/lca/products/create';
  if (!props.canCreate) return <Text>{t('product.noAvailable')}</Text>;
  return (
    <BoxLink to={link}>
      <Text>
        {t('product.createNew')} {'>'}
      </Text>
      <div
        style={{
          marginTop: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EmptyIcon />
      </div>
    </BoxLink>
  );
}

interface Props {
  id: number;
  name: string;
  url: string;
  period: string;
  materials: LcaProductMaterial[];
  last_edited: string;
  canDelete: boolean;
  passportAccessToken: string;
  impactData?: ProductImpactOutSchema | null;
  isLoadingImpact?: boolean;
  impactError?: unknown;
  imageUrl?: string;
  // sub-folder the product sits in, relative to the folder being browsed
  folderLabel?: string;
  selectable?: boolean;
  selected?: boolean;
  // eslint-disable-next-line no-unused-vars
  onToggleSelected?: (productId: number) => void;
  // eslint-disable-next-line no-unused-vars
  onMoveToFolder?: (productId: number) => void;
}

const Materials = styled.div<{ color: string }>`
  color: var(--Dark, #281302);
  text-align: center;
  height: 32px;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.28px;
  background-color: ${({ color }) => color};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px 12px;
  width: fit-content;
`;

const Period = styled.p`
  color: var(--Dark-grey, #4d4d4d);
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.54px;
  margin-top: 12px;
  margin-bottom: 0;
`;

const EmissionsContainer = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const InlineEmissionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const InlineEmissionItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 4px;
  padding: 4px 8px;
  min-width: fit-content;
`;

const EmissionHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #281302;
  margin-right: 6px;
`;

const EmissionValue = styled.div`
  font-size: 11px;
  color: #4d4d4d;
  font-weight: 500;
`;

const InlineSummaryContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  border-radius: 4px;
  padding: 6px 8px;
  border-top: 1px solid #dadada;
`;

const SummaryHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #281302;
`;

const SummaryValue = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #281302;
`;

const LoadingText = styled.div`
  color: #4d4d4d;
  font-size: 12px;
  text-align: center;
  padding: 16px;
`;

const ImpactErrorText = styled(LoadingText)`
  color: #b42318;
`;

const ProductImageContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: absolute;
  top: 16px;
  right: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const getDeleteProductErrorToastKey = (error: any) =>
  error?.response?.status === 409 &&
  error?.response?.data?.detail === 'lca_product_used_as_sub_product'
    ? 'product.modals.delete.toasts.usedAsSubProduct'
    : 'error';

type ModalState = 'closed' | 'delete' | 'confirm_delete' | 'copy';

export function ProductsItemsCreated({
  name,
  url,
  materials,
  period,
  last_edited,
  id,
  passportAccessToken,
  canDelete,
  impactData,
  isLoadingImpact,
  impactError,
  imageUrl,
  folderLabel,
  selectable = false,
  selected = false,
  onToggleSelected,
  onMoveToFolder,
}: Props) {
  const { t } = useTranslation('lca');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [buttonVisible, setButtonVisible] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('closed');
  const navigate = useNavigate();

  const actions = useProductActions(id);

  const handleDeleteModal1ButtonBClick = () => {
    actions.deleteProduct
      .mutateAsync()
      .then(() =>
        toast.success(t('product.modals.delete.toasts.success') as string)
      )
      .catch((error: any) =>
        toast.error(t(getDeleteProductErrorToastKey(error)) as string)
      );
    setModalState('closed');
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setButtonVisible(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setButtonVisible(false);
  };

  const renderEmissionsDisplay = () => {
    const impactErrorMessage = getLcaApiErrorMessage(impactError, t);
    if (impactErrorMessage) {
      return (
        <EmissionsContainer>
          <ImpactErrorText>{impactErrorMessage}</ImpactErrorText>
        </EmissionsContainer>
      );
    }

    if (isLoadingImpact) {
      return (
        <EmissionsContainer>
          <LoadingText>{t('loading')}</LoadingText>
        </EmissionsContainer>
      );
    }

    if (!impactData) {
      return null;
    }

    const { emission_per_stage, total_emission, area } = impactData;

    // Get all LCA stages for display
    const allStages = [
      {
        key: 'A1',
        stage: LcaDiagramColumns.rawMaterialsExtractionAndProcessing,
      },
      { key: 'A2', stage: LcaDiagramColumns.transportOfRawMaterials },
      { key: 'A3', stage: LcaDiagramColumns.productionAndPackaging },
      { key: 'A4', stage: LcaDiagramColumns.distributionAndStorage },
      { key: 'A5', stage: LcaDiagramColumns.installation },
      { key: 'B1', stage: LcaDiagramColumns.useStage },
      { key: 'C1-C4', stage: LcaDiagramColumns.endOfLife },
    ];

    // Show all stages that have values > 0, or always show A1-A3 (main cradle-to-gate stages)
    const displayStages = allStages.filter(({ stage, key }) => {
      const value = emission_per_stage[stage];
      const hasValue = value !== null && value !== undefined && value > 0;
      const isMainStage = ['A1', 'A2', 'A3'].includes(key);
      return hasValue || isMainStage;
    });

    return (
      <EmissionsContainer>
        <InlineEmissionsContainer>
          {displayStages.map(({ key, stage }) => {
            const value = emission_per_stage[stage];
            return (
              <InlineEmissionItem key={key}>
                <EmissionHeader>{key}</EmissionHeader>
                <EmissionValue>
                  <EcoinventEmission value={value} />
                </EmissionValue>
              </InlineEmissionItem>
            );
          })}
        </InlineEmissionsContainer>
        <InlineSummaryContainer>
          <SummaryHeader>{t('diagram.summary')}</SummaryHeader>
          <SummaryValue>
            <EcoinventEmission value={total_emission} /> {area?.unit_name || ''}
          </SummaryValue>
        </InlineSummaryContainer>
      </EmissionsContainer>
    );
  };

  const renderProductImage = () => {
    if (!imageUrl) {
      return null;
    }

    return (
      <Link to={url}>
        <ProductImageContainer>
          <ProductImage
            src={imageUrl}
            alt={name}
            onError={(e) => {
              // Hide image if it fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        </ProductImageContainer>
      </Link>
    );
  };

  return (
    <>
      <BoxDiv $buttonVisible={buttonVisible} $selected={selectable && selected}>
        {selectable && (
          <SelectCheckbox>
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelected?.(id)}
              inputProps={{ 'aria-label': name }}
            />
          </SelectCheckbox>
        )}
        <Link to={url}>
          <Text>
            <TextWithTooltip text={name} minLength={32} />
          </Text>
        </Link>
        {!!folderLabel && (
          <FolderLabel title={t('folders.inSubfolder', { path: folderLabel })}>
            <FolderOutlined fontSize="inherit" />
            <span>{folderLabel}</span>
          </FolderLabel>
        )}
        {renderProductImage()}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <Link to={url}>
              <Period>{period}</Period>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  margin: '12px 0 8px 0',
                  flexWrap: 'wrap',
                }}
              >
                {materials.map((el) => (
                  <Materials key={el.id} color="#79BBAF">
                    {el.material_name}
                  </Materials>
                ))}
              </div>
            </Link>
            {renderEmissionsDisplay()}
          </div>
          <StyledFlex
            style={{
              width: '100%',
              borderTop: '1px solid #dadada',
              marginTop: 'auto',
              paddingTop: '10px',
            }}
          >
            <Typography variant="captionText" color="text.secondary">
              {`${t('recentUpdate')} ${new Date(
                last_edited
              ).toLocaleDateString()}`}
            </Typography>

            <div>
              <IconButton
                onClick={() => navigate(`/lca/products/${id}/report-builder`)}
              >
                <DownloadOutlined />
              </IconButton>
              <IconButton
                onClick={() =>
                  navigate(`/lca/products/${id}/share-product-passport`)
                }
              >
                <ShareOutlined />
              </IconButton>
              {canDelete && (
                <HiddenIconButton
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  onClick={handleClick}
                >
                  <MoreVertIcon />
                </HiddenIconButton>
              )}
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  onClick={() =>
                    window.open(
                      `/lca/product-passport/${passportAccessToken}`,
                      '_blank'
                    )
                  }
                >
                  {t('product.openPassport')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    actions.exportToExcel.mutate({
                      defaultFilename: `${name}.xlsx`,
                    });
                    handleClose();
                  }}
                >
                  {t('product.exportToExcel')}
                </MenuItem>

                <MenuItem
                  onClick={() => navigate(`/lca/products/${id}/report-builder`)}
                >
                  {t('product.exportDocx')}
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setModalState('copy');
                    handleClose();
                  }}
                >
                  {t('product.copy')}
                </MenuItem>
                {!!onMoveToFolder && (
                  <MenuItem
                    onClick={() => {
                      onMoveToFolder(id);
                      handleClose();
                    }}
                  >
                    {t('selection.move')}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setModalState('delete');
                    handleClose();
                  }}
                >
                  {t('product.delete')}
                </MenuItem>
              </Menu>
            </div>
          </StyledFlex>
        </div>
      </BoxDiv>

      <ConfirmationModal
        open={modalState === 'delete'}
        titleKey={t('product.modals.delete.title')}
        textKey={t('product.modals.delete.desc')}
        trueButtonTextKey={t('product.modals.delete.buttons.yes')}
        falseButtonTextKey={t('product.modals.delete.buttons.no')}
        onTrue={() => setModalState('confirm_delete')}
        onFalse={() => setModalState('closed')}
        onClose={() => setModalState('closed')}
      />
      <ConfirmationModal
        open={modalState === 'confirm_delete'}
        titleKey={t('product.modals.confirmDelete.title')}
        textKey={t('product.modals.confirmDelete.desc')}
        trueButtonTextKey={t('product.modals.confirmDelete.buttons.yes')}
        falseButtonTextKey={t('product.modals.confirmDelete.buttons.no')}
        onTrue={handleDeleteModal1ButtonBClick}
        onFalse={() => setModalState('closed')}
        onClose={() => setModalState('closed')}
      />
      <CopyProductModal
        open={modalState === 'copy'}
        onClose={() => setModalState('closed')}
        productId={id}
        productName={name}
      />
    </>
  );
}
